import * as React from 'react'

import styled from '@theme/styled-components'
import Theme from '@theme/Theme'

import { FaMapMarker } from 'react-icons/fa'
import { HiCheck } from 'react-icons/hi'
import { BsFillDiamondFill, BsExclamationDiamondFill, BsExclamation, BsFillStopFill } from 'react-icons/bs'

interface Props {
  type: BusinessDetails['state']
  source: 'OSR' | 'SIROCCO'
  size?: number
  lat?: number
  lng?: number
  onClick?: () => void
  title?: string
  focused: boolean
  hideTitle: boolean
  expandedRadius?: number
  expandedNumber?: number
}

const LINE_WIDTH = 200

const Marker = ({
  type,
  size = 25,
  onClick,
  title,
  focused,
  hideTitle,
  expandedRadius,
  expandedNumber,
  source,
}: Props) => {
  const markerColor =
    type === 'inprogress'
      ? Theme.colors.markerInProgress
      : type === 'stopped'
      ? Theme.colors.markerStopped
      : type === 'ended'
      ? Theme.colors.markerEnded
      : Theme.colors.markerBackground

  return (
    <>
      <Container size={size} onClick={onClick} expandedRadius={expandedRadius} expandedNumber={expandedNumber}>
        {expandedRadius && expandedNumber && (
          <svg
            width={LINE_WIDTH}
            height={size}
            style={{
              position: 'absolute',
              top: 0,
              left: (size - LINE_WIDTH) / 2,
              transform: `rotate(${(-0.5 - (2 * expandedRadius) / expandedNumber) * Math.PI}rad)`,
            }}>
            <path
              d={`M ${LINE_WIDTH * 0.5} ${size * 0.5} L ${LINE_WIDTH} ${size * 0.5}`}
              stroke="black"
              strokeDasharray="10,5,4,5"
            />
          </svg>
        )}
        {type === 'inprogress'
          ? inProgressMarker(size, source)
          : type === 'stopped'
          ? stoppedMarker(size, source)
          : type === 'ended'
          ? endedMarker(size, source)
          : null}
      </Container>
      {focused && !hideTitle && (
        <>
          <LibelleContainer size={size} markerColor={markerColor}>
            <Libelle markerColor={markerColor}>{title}</Libelle>
          </LibelleContainer>
          <Triangle size={size} />
        </>
      )}
    </>
  )
}
export default Marker

const Container = styled('div')<{ size: number; expandedRadius?: number; expandedNumber?: number }>`
  margin: 5px;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  top: ${(props) =>
    !props.expandedRadius || !props.expandedNumber
      ? -props.size
      : -props.size + 100 * Math.cos((props.expandedRadius * Math.PI) / (props.expandedNumber * 0.5))}px;
  width: ${(props) => props.size}px;
  height: ${(props) => props.size}px;
  left: ${(props) =>
    !props.expandedRadius || !props.expandedNumber
      ? -props.size / 2
      : -props.size / 2 + 100 * Math.sin((props.expandedRadius * Math.PI) / (props.expandedNumber * 0.5))}px;
  cursor: pointer;
`
const LibelleContainer = styled('div')<{ size: number; markerColor: string }>`
  position: relative;
  width: fit-content;
  background: ${(props) => props.theme.colors.markerBackground};
  border-radius: 15px;
  top: ${(props) => -4.5 * props.size}px;
  left: ${(props) => -props.size / 2 - 115}px;
  box-shadow: 0px 0px 15px ${(props) => props.markerColor + '80'};
`
const Libelle = styled('div')<{ markerColor: string }>`
  text-align: center;
  padding: 10px;
  width: 250px;

  ${(props) => props.theme.fonts.markerLibelle};
  color: ${(props) => props.markerColor};
`
const Triangle = styled('div')<{ size: number }>`
  top: ${(props) => -4.5 * props.size}px;
  border-right: 10px solid transparent;
  border-left: 10px solid transparent;
  border-top: 15px solid ${(props) => props.theme.colors.markerBackground};
  display: inline-block;
  position: relative;
  right: 5px;
  height: 0;
  width: 0;
`

const SHADOW_STYLE = {
  boxShadow: '0px 50px 0px -30px rgba(0,0,0,1)',
}

const inProgressMarker = (size: number, source: 'OSR' | 'SIROCCO') =>
  source === 'SIROCCO' ? (
    <>
      <FaMapMarker size={size} color={Theme.colors.markerInProgress} />
      <HiCheck
        size={size * 0.5}
        color="white"
        style={{
          zIndex: 1,
          position: 'absolute',
          marginBottom: size * 0.25,
          ...SHADOW_STYLE,
        }}
      />
    </>
  ) : (
    <>
      <BsFillDiamondFill size={size * 0.8} color={Theme.colors.markerInProgress} />
      <HiCheck
        size={size * 0.5}
        color="white"
        style={{
          zIndex: 1,
          position: 'absolute',
          ...SHADOW_STYLE,
        }}
      />
    </>
  )

const stoppedMarker = (size: number, source: 'OSR' | 'SIROCCO') =>
  source === 'SIROCCO' ? (
    <>
      <FaMapMarker size={size} color={Theme.colors.markerStopped} />
      <BsExclamation
        size={size * 0.75}
        color="white"
        style={{
          zIndex: 1,
          position: 'absolute',
          marginBottom: size * 0.25,
          ...SHADOW_STYLE,
        }}
      />
    </>
  ) : (
    <>
      <BsFillDiamondFill size={size * 0.8} color="white" />
      <BsExclamationDiamondFill
        size={size * 0.8}
        color={Theme.colors.markerStopped}
        style={{
          zIndex: 1,
          position: 'absolute',
          ...SHADOW_STYLE,
        }}
      />
    </>
  )

const endedMarker = (size: number, source: 'OSR' | 'SIROCCO') =>
  source === 'SIROCCO' ? (
    <>
      <FaMapMarker size={size} color={Theme.colors.markerEnded} />
      <BsFillStopFill
        size={size * 0.5}
        color="white"
        style={{
          zIndex: 1,
          position: 'absolute',
          marginBottom: size * 0.25,
          ...SHADOW_STYLE,
        }}
      />
    </>
  ) : (
    <>
      <BsFillDiamondFill size={size * 0.8} color={Theme.colors.markerEnded} />
      <BsFillStopFill
        size={size * 0.4}
        color="white"
        style={{
          zIndex: 1,
          position: 'absolute',
          ...SHADOW_STYLE,
        }}
      />
    </>
  )

export { inProgressMarker, stoppedMarker, endedMarker }
